import { createFileRoute, Link } from '@tanstack/react-router'
import { useId, useState } from 'react'
import { Panel } from '@/components/panel'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useCreatePrompt, usePrompts } from '@/lib/admin-ai'
import { ApiError } from '@/lib/api'
import { formatExact } from '@/lib/format'
import { Age, SkeletonRows, Tag, Th } from '../../-parts'

export const Route = createFileRoute('/_app/admin/ai/_list/prompts')({
  component: PromptsPage,
})

/**
 * The named slots, and what is running in each.
 *
 * A slot is a name with a pointer at the wording that runs; the wordings
 * themselves are frozen and live one click in. A slot with no version yet is a
 * legible state rather than a half-written prompt, which is why minting the two
 * is two steps.
 */
function PromptsPage() {
  const { data, isPending } = usePrompts()
  const now = Date.now()

  return (
    <div className="space-y-4">
      <Panel title="Prompts" meta={data ? formatExact(data.prompts.length) : undefined} flush>
        {isPending ? (
          <SkeletonRows rows={4} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="border-b border-border bg-secondary/30">
                  <Th align="left">Key</Th>
                  <Th align="left" className="w-[160px]">
                    Scope
                  </Th>
                  <Th align="left" className="w-[200px]">
                    Running
                  </Th>
                  <Th className="w-[90px]">Versions</Th>
                  <Th className="w-[80px]">Runs</Th>
                  <Th className="w-[80px]">Updated</Th>
                </tr>
              </thead>
              <tbody>
                {data?.prompts.map((prompt) => (
                  <tr key={prompt.id} className="border-b border-border last:border-b-0">
                    <td className="py-2 pr-3 pl-3">
                      <Link
                        to="/admin/ai/prompts/$promptId"
                        params={{ promptId: prompt.id }}
                        className="font-mono text-[12.5px] hover:text-link"
                      >
                        {prompt.key}
                      </Link>
                      {prompt.purpose && (
                        <span className="block max-w-[420px] truncate text-[11.5px] text-muted-foreground">
                          {prompt.purpose}
                        </span>
                      )}
                    </td>

                    {/* No workspace is a prompt the product ships; a workspace
                        makes it theirs to edit. */}
                    <td className="px-3 py-2">
                      {prompt.organizationId === null ? (
                        <Tag>shipped</Tag>
                      ) : (
                        <>
                          <span className="block max-w-[150px] truncate text-[12px]">
                            {prompt.organization}
                          </span>
                          {prompt.host && (
                            <span className="block max-w-[150px] truncate font-mono text-[11px] text-muted-foreground">
                              {prompt.host}
                            </span>
                          )}
                        </>
                      )}
                    </td>

                    <td className="px-3 py-2">
                      {prompt.activeVersion === null ? (
                        <span className="font-mono text-[12px] text-muted-foreground">
                          no version
                        </span>
                      ) : (
                        <span className="flex flex-wrap items-center gap-1.5">
                          <Tag tone="plain">{`v${prompt.activeVersion}`}</Tag>
                          <span className="max-w-[150px] truncate font-mono text-[11.5px] text-muted-foreground">
                            {prompt.activeModelId}
                          </span>
                        </span>
                      )}
                    </td>

                    <td className="px-3 py-2 text-right font-mono text-[12px] tabular">
                      {formatExact(prompt.versions)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[12px] tabular">
                      {formatExact(prompt.runs)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Age at={prompt.updatedAt} now={now} />
                    </td>
                  </tr>
                ))}
                {data?.prompts.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                      No prompt slots yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <NewSlot />
    </div>
  )
}

function NewSlot() {
  const create = useCreatePrompt()
  const keyId = useId()
  const purposeId = useId()
  const orgId = useId()
  const [key, setKey] = useState('')
  const [purpose, setPurpose] = useState('')
  const [organizationId, setOrganizationId] = useState('')

  return (
    <Panel title="New slot">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          if (!key) return
          create.mutate(
            {
              key,
              ...(purpose ? { purpose } : {}),
              ...(organizationId ? { organizationId } : {}),
            },
            {
              onSuccess: () => {
                setKey('')
                setPurpose('')
                setOrganizationId('')
              },
            },
          )
        }}
      >
        <label className="space-y-1" htmlFor={keyId}>
          <span className="mono-label block text-muted-foreground">Key</span>
          <Input
            id={keyId}
            value={key}
            onChange={(event) => setKey(event.target.value)}
            placeholder="report.summary"
            className="h-9 w-[220px] font-mono text-[13px]"
          />
        </label>

        <label className="space-y-1" htmlFor={purposeId}>
          <span className="mono-label block text-muted-foreground">Purpose</span>
          <Input
            id={purposeId}
            value={purpose}
            onChange={(event) => setPurpose(event.target.value)}
            className="h-9 w-[320px] text-[13px]"
          />
        </label>

        <label className="space-y-1" htmlFor={orgId}>
          <span className="mono-label block text-muted-foreground">Workspace</span>
          <Input
            id={orgId}
            value={organizationId}
            onChange={(event) => setOrganizationId(event.target.value)}
            className="h-9 w-[220px] font-mono text-[13px]"
          />
        </label>

        <Button size="sm" type="submit" disabled={!key || create.isPending}>
          Mint
        </Button>

        {create.error && (
          <p className="font-mono text-[11.5px] text-destructive">
            {create.error instanceof ApiError ? create.error.message : 'Could not mint that slot'}
          </p>
        )}
      </form>
    </Panel>
  )
}
